import get from "lodash/get"
import React from "react"

type MassageFn = (results: any) => any

// Note: Algolia is currently used only for Library search/filter functionality.
// Refactoring to this component, globals.d.ts typings, and potentially useAlgoliaSearch consumers
// would be required to extend its use to other data sources.
interface RunSearchArgs {
  indices: string[]
  query: string | null | undefined
  authorFacetValues?: string[]
  categoryFacetValues?: string[]
  massageResults?: MassageFn
}

interface AlgoliaContextValue {
  results: any | null
  loading: boolean
  error: Error | null
  algoliaEnabled: boolean
  runSearch: (args: RunSearchArgs) => Promise<void>
}

const AlgoliaContext = React.createContext<AlgoliaContextValue | null>(null)

export const AlgoliaProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const [results, setResults] = React.useState<any | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | null>(null)

  const searchKeys = get(window.bootstrappedData, "integrations.algolia")
  const algoliaEnabled =
    !!window.algoliasearch &&
    !!searchKeys &&
    typeof searchKeys === "object"

  // Shared client instance
  const clientRef = React.useRef<any>(null)
  React.useEffect(() => {
    if (algoliaEnabled && !clientRef.current && window.algoliasearch) {
      clientRef.current = window.algoliasearch(
        "UTMZ56WOUH",
        "1565fef5ce85305f58c3b5cc28915436"
      )
    }
  }, [algoliaEnabled])

  const runSearch = React.useCallback(
    async ({
      indices,
      query,
      authorFacetValues,
      categoryFacetValues,
      massageResults
    }: RunSearchArgs) => {
      if (!algoliaEnabled || !clientRef.current) return
      // if query is null, clear results. Happens when search term is cleared from input.
      if (!query) {
        setResults(null)
        return
      }

      const authorsFacetFilters = (authorFacetValues || []).map(
        (v) => `authors.display_name:${v}`
      )
      const categoriesFacetFilters = (categoryFacetValues || []).map(
        (v) => `categories.name:${v}`
      )
      const facetFilters =
        [...authorsFacetFilters, ...categoriesFacetFilters].join(",") || null

      // build queries
      const queries = indices.reduce<any[]>((acc, index) => {
        const entry = searchKeys[index as keyof typeof searchKeys]
        if (!entry) return acc
        const { params } = entry
        if (params) {
          delete params["restrictIndices"]
          params.facets = "*"
          params.facetFilters = facetFilters
        }
        const indexName = `${searchKeys.index_prefix}${index}`
        return [
          ...acc,
          {
            indexName,
            query: query || "",
            params
          }
        ]
      }, [])

      if (!queries.length) {
        setResults(null)
        return
      }

      setLoading(true)
      setError(null)
      try {
        const { results: algoliaResults } = await new Promise<any>((resolve, reject) =>
          clientRef.current.search(queries, (err: any, res: any) =>
            err ? reject(err) : resolve(res)
          )
        )
        const finalResults = massageResults
          ? massageResults(algoliaResults)
          : algoliaResults
        setResults(finalResults)
      } catch (e: any) {
        setError(e)
      } finally {
        setLoading(false)
      }
    },
    [algoliaEnabled, searchKeys]
  )

  const value = React.useMemo<AlgoliaContextValue>(
    () => ({ results, loading, error, algoliaEnabled, runSearch }),
    [results, loading, error, algoliaEnabled, runSearch]
  )

  return (
    <AlgoliaContext.Provider value={value}>{children}</AlgoliaContext.Provider>
  )
}

// Consumer hook that mirrors the old signature but delegates to the provider
export const useAlgoliaSearch = ({
  query,
  indices = [],
  authorFacetValues,
  categoryFacetValues,
  massageResults
}: {
  query: string | null | undefined
  indices?: string[]
  authorFacetValues?: string[]
  categoryFacetValues?: string[]
  massageResults?: MassageFn
}) => {
  const ctx = React.useContext(AlgoliaContext)
  if (!ctx) {
    return {
      results: null,
      loading: false,
      error: null,
      algoliaEnabled: false
    }
  }

  const { results, loading, error, algoliaEnabled, runSearch } = ctx

  React.useEffect(() => {
    if (!query) {
      // Clear Algolia results so GQL data can take over
      runSearch({ indices, query: null })
      return
    }
    runSearch({
      indices,
      query,
      authorFacetValues,
      categoryFacetValues,
      massageResults
    })
  }, [
    runSearch,
    indices,
    query,
    authorFacetValues,
    categoryFacetValues,
    massageResults
  ])

  return { results, loading, error, algoliaEnabled }
}
